import * as React from "react";
import { connect } from "react-redux";
import * as S from "@styled";
import styled from "styled-components";
import theme from "styled-theming";

import { DispatchType, Utilities } from "../../shared/utilities";
import * as SaveProcessActions from "../actions/SaveProcess";
import { State } from "../model/State";
import { handleToggleDropdown } from "shared/styles/helpers";
import { Permissions } from "shared/model/AllPermissions";
import DropdownButton from "shared/components/DropdownButton";
import { ThemeType } from "shared/styles/theme";

const addKeypressHandler = (handleEvent: (ev: KeyboardEvent) => void) => {
    document.addEventListener('keydown', handleEvent);
}

const removeKeypressHandler = (handleEvent: (ev: KeyboardEvent) => void) => {
    document.removeEventListener('keydown', handleEvent);
}

const dropdownButtonLeftBorder = theme("mode", {
    [ThemeType.light]: `2px solid ${S.Colors.purpleSecondary}`,
    [ThemeType.dark]: `2px solid ${S.Colors.grey_2}`,
})

const dropdownButtonLeftBorderHover = theme("mode", {
    [ThemeType.light]: `2px solid ${S.Colors.purplePrimary}`,
    [ThemeType.dark]: `2px solid ${S.Colors.grey_2}`,
})

const StyledButtonContainer = styled(S.FlexContainer)`
    #editorCreateWithoutActivateMenuItem, #editorSaveButton {
        &:hover + div #process-save-options-dropdown {
            border-left: ${dropdownButtonLeftBorderHover} !important;
        }
    } 
    
    #process-save-options-dropdown {
        border-top-left-radius: 0px;
        border-bottom-left-radius: 0px;
        border-left: ${dropdownButtonLeftBorder} !important;
        padding: 0 20px 0 15px;

        &:hover {
            border-left: ${dropdownButtonLeftBorderHover} !important;
        }
    }
`

export const processSaveOptionsDropdownRefName = "process-save-options";

export const SaveProcessForm: React.FC<SaveProcessFormProps> = (props) => {

    const getShouldShow = (): boolean => {
        if (props.processIsDirty || props.isDraft) {
            return true;
        } else if (props.disableEdit && props.hasProcessEditPermissions) {
            return true;
        } else {
            return false;
        }
    }

    const [shouldShow, setShouldShow] = React.useState<boolean>(getShouldShow());

    React.useEffect(() => {
        setShouldShow(getShouldShow());
    }, [props.processIsDirty, props.isDraft, props.isNew, props.disableEdit, props.hasProcessEditPermissions]);

    const renderActionButtons = () => {
        if (props.disableEdit && props.hasProcessEditPermissions) {
            return (
                <S.LinkButton variant={S.ButtonType.dark} href={`${props.isSubProcess ? "SubProcess" : "Process"}/Edit/${props.uniqueIdentifier}`}>
                    {FRENDS.Localizations.EditLatestVersion}
                </S.LinkButton>
            )
        } else {
            return <SaveOrCreateNewButtons {...props} />;
        }
    }

    return (
        <S.BottomBar isShown={shouldShow}>
            {renderActionButtons()}
        </S.BottomBar>
    )
}

const SaveOrCreateNewButtons: React.FC<SaveProcessFormProps> = (props) => {
    const [description, setDescription] = React.useState<string>("");

    React.useEffect(() => {
        // Add the keypress handler
        addKeypressHandler(handleKeydownEvent);
        return () => {
            removeKeypressHandler(handleKeydownEvent)
        };
    }, []);

    const handleChangeDescriptionChange = (e: React.FormEvent<any>) => {
        setDescription(e.currentTarget.value);
    }

    const handleSave = () => {
        props.onSave(description);
    }

    const handleSaveAndRun = () => {
        props.onSave(description, true);
    }

    const handleCreateAndActivate = () => {
        props.onCreate(description, true);
    }

    const handleCreateWithoutActivate = () => {
        props.onCreate(description, false);
    }

    const handleSaveDraft = () => {
        props.onSaveDraft(description);
    }

    const handleKeydownEvent = (ev: KeyboardEvent) => {
        if (!props.isSaving && !ev.repeat && (ev.ctrlKey || ev.metaKey) && ev.key === 's') {
            ev.preventDefault();
            handleSaveDraft();
        }
    }

    const isNewProcess = props.isNew && !props.isSubProcess;

    const primaryButtonSplitStyles: React.CSSProperties = { marginLeft: '12px', borderTopRightRadius: "0px", borderBottomRightRadius: "0px" };

    const primaryButton = isNewProcess
        ? (
            <S.Button
                disabled={props.isSaving}
                variant={S.ButtonType.dark}
                style={primaryButtonSplitStyles}
                id="editorCreateWithoutActivateMenuItem"
                onClick={handleCreateWithoutActivate}
                title="Creates the Process without activating Triggers"
            >
                {FRENDS.Localizations.CreateNew} {FRENDS.Localizations.Process}
            </S.Button>
        )
        : (
            <S.Button disabled={props.isSaving} variant={S.ButtonType.dark} style={primaryButtonSplitStyles} id="editorSaveButton" onClick={handleSave}>{FRENDS.Localizations.SaveChanges}</S.Button>
        );

    let createAndActivateButton = null;
    if (isNewProcess) {
        createAndActivateButton = (
            <button
                disabled={props.isSaving}
                id="editorCreateAndActivateButton"
                onClick={handleCreateAndActivate}
                title="Creates the Process and activates its' Triggers"
            >
                Create and activate
            </button>
        );
    }

    return (
        <StyledButtonContainer style={{ width: "50%" }} justifyCenter={true}>
            <S.Input
                disabled={props.isSaving}
                style={{ marginRight: "15px", minWidth: "300px" }}
                type="text"
                placeholder={FRENDS.Localizations.Description}
                onChange={handleChangeDescriptionChange}
            />
            <S.Button disabled={props.isSaving} id="editorValidateButton" variant={S.ButtonType.bordered} onClick={props.onValidateBuild}>{FRENDS.Localizations.Validate_Build}</S.Button>
            {primaryButton}
            <DropdownButton
                id={processSaveOptionsDropdownRefName}
                buttonComponent={(
                    <S.Button disabled={props.isSaving} type="button" className="dropbtn" variant={S.ButtonType.dark} id="process-save-options-dropdown" title="More actions" onClick={() => handleToggleDropdown(processSaveOptionsDropdownRefName)}>
                        <i className="ci-chevron_big_down" />
                    </S.Button>
                )}
                dropDownContentCustomStyles={{ bottom: "70px", right: "20px" }}
            >
                {createAndActivateButton}
                <button disabled={props.isSaving} id="editorSaveDraftMenuItem" onClick={handleSaveDraft}>
                    {props.isDraft ? FRENDS.Localizations.OnlySave : FRENDS.Localizations.CreateNew} {FRENDS.Localizations.Draft}
                </button>
                {!props.isDraft &&
                    <button disabled={props.isSaving} id="editorSaveAndRundMenuItem" onClick={handleSaveAndRun}>
                        Save changes and run
                    </button>
                }
            </DropdownButton>
        </StyledButtonContainer>
    );
}

interface SaveProcessFormStateProps {
    processIsDirty: boolean | undefined;
    disableEdit: boolean | undefined;
    isSaving: boolean | undefined;
    uniqueIdentifier: string | undefined;
    isSubProcess: boolean | undefined;
    isNew: boolean | undefined;
    isDraft: boolean | undefined;
    hasProcessEditPermissions: boolean | undefined;
}

interface SaveProcessFormDispatchProps {
    onCreate: (changeDescription: string, activate: boolean) => void;
    onSave: (changeDescription: string, runImmediately?: boolean) => void;
    onSaveDraft: (changeDescription: string) => void;
    onValidateBuild: () => void;
}

export type SaveProcessFormProps = SaveProcessFormStateProps & SaveProcessFormDispatchProps;

const mapStateToProps = (state: State): SaveProcessFormStateProps => {
    return {
        processIsDirty: state.uiState.isDirty || state.process.isMigration,
        disableEdit: state.uiState.disableEdit,
        uniqueIdentifier: state.process.uniqueIdentifier,
        isSaving: state.uiState.isSaving,
        isSubProcess: state.process.isSubprocess,
        isDraft: state.process.isDraft,
        isNew: state.process.id === 0 || (state.process.isDraft && state.process.idOfBaseProcessForDraft === 0),
        hasProcessEditPermissions: Utilities.getUserPermission(Permissions.ProcessEdit)
    };
};

const mapDispatchToProps = (dispatch: DispatchType<State>): SaveProcessFormDispatchProps => {
    return {
        onCreate: (changeDescription: string, activate: boolean) => dispatch(SaveProcessActions.saveProcess(changeDescription, activate)),
        onSave: (changeDescription: string, runImmediately?: boolean) => dispatch(SaveProcessActions.saveProcess(changeDescription, false, runImmediately)),
        onSaveDraft: (changeDescription: string) => dispatch(SaveProcessActions.saveProcessDraft(changeDescription)),
        onValidateBuild: () => dispatch(SaveProcessActions.buildProcess()),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SaveProcessForm);
